/**
 * InboxWatcher — Aug-2026 (User-Wunsch): GLOBALE Ordner-Überwachung des
 * Dokumenten-Centers. EIN überwachter Ordner für ALLE Dokumente — die KI
 * klassifiziert zuerst (Lieferanten-Beleg vs. Kunden-Dokument) und routet
 * automatisch in die passende Pipeline. Unsichere Fälle warten auf eine
 * 1-Klick-Entscheidung im Dokumenten-Center (/dealer/documents).
 */
import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "./api";
import { useAuth } from "./auth";
import { setHandoffFile, setHandoffCleanup } from "./fileHandoff";
import { scanFolderSupported, getDocCenterFolder, getStoredDocCenterFolder, listOutboxFiles, deleteOutboxFile } from "./scanFolder";
import { runScanJob } from "./scanJobs";
import { useWatchHeartbeat } from "./useWatchHeartbeat";

const InboxWatcherContext = createContext(null);
export const useInboxWatcher = () => useContext(InboxWatcherContext);

const DOC_TYPES = ["delivery_note", "invoice", "combined", "control_only"];

export function shapeItem(data, fileName) {
    const ct = data.doc_type === "control_only";
    return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        fileName,
        scan: data,
        docType: DOC_TYPES.includes(data.doc_type) ? data.doc_type : "delivery_note",
        commissionMode: !ct && !!data.is_commission,
        commissionTarget: (data.commission_suggestions || [])[0] || null,
        decisionRequired: !ct && !!data.commission_decision_required,
        decisionMade: ct || !data.commission_decision_required,
    };
}

export function InboxWatcherProvider({ children }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [folderName, setFolderName] = useState("");
    const [watching, setWatching] = useState(false);
    // Aug-2026 (User-Fund): Bei 2 offenen Tabs verarbeiteten BEIDE dieselbe Datei
    // (einer buchte, der andere meldete „bereits verbucht"). Web-Locks-API wählt
    // jetzt EINEN führenden Tab; schließt er, übernimmt automatisch der nächste.
    const [isLeader, setIsLeader] = useState(false);
    const [needsReconnect, setNeedsReconnect] = useState(false);
    const [autoBook, setAutoBook] = useState(true);
    const [pending, setPending] = useState([]); // Lieferanten-Belege zur Prüfung
    const [pendingCustomer, setPendingCustomer] = useState([]); // Kunden-Dokumente zur Zuordnung
    const [pendingUnsure, setPendingUnsure] = useState([]); // KI unsicher → 1-Klick-Entscheidung
    const [analyzing, setAnalyzing] = useState([]); // [{name, phase}]
    const [booking, setBooking] = useState(null);
    const [log, setLog] = useState([]);
    const [historyReload, setHistoryReload] = useState(0);
    const handleRef = useRef(null);
    const processedRef = useRef(new Set());
    const failsRef = useRef(new Map());
    const pendingRef = useRef([]);
    const customerRef = useRef([]);
    const unsureRef = useRef([]);
    const busyRef = useRef(false);
    const autoBookRef = useRef(true);
    useEffect(() => { autoBookRef.current = autoBook; }, [autoBook]);

    const isDealer = !!user && (user.role === "b2b" || user.role === "zweiradverkaeufer");

    useWatchHeartbeat("inbox", watching && isLeader, folderName);

    useEffect(() => {
        if (!navigator.locks?.request) { setIsLeader(true); return undefined; }
        let cancelled = false;
        let release = null;
        navigator.locks.request("tw-doccenter-watcher", () => new Promise((resolve) => {
            if (cancelled) { resolve(); return; }
            release = resolve;
            setIsLeader(true);
        })).catch(() => setIsLeader(true));
        return () => { cancelled = true; if (release) release(); };
    }, []);

    const setPendingSync = useCallback((updater) => {
        setPending((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            pendingRef.current = next;
            return next;
        });
    }, []);
    const setCustomerSync = useCallback((updater) => {
        setPendingCustomer((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            customerRef.current = next;
            return next;
        });
    }, []);
    const setUnsureSync = useCallback((updater) => {
        setPendingUnsure((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            unsureRef.current = next;
            return next;
        });
    }, []);

    const addLog = useCallback((entry) => setLog((prev) => [{ ...entry, at: new Date() }, ...prev].slice(0, 50)), []);

    const setPhase = useCallback((name, phase) => setAnalyzing((prev) => {
        const rest = prev.filter((a) => a.name !== name);
        return phase ? [...rest, { name, phase }] : rest;
    }), []);

    const deleteIfNoPending = useCallback(async (fileName) => {
        if (!handleRef.current) return false;
        if (pendingRef.current.some((p) => p.fileName === fileName)
            || customerRef.current.some((p) => p.fileName === fileName)
            || unsureRef.current.some((p) => p.fileName === fileName)) return false;
        return deleteOutboxFile(handleRef.current, fileName);
    }, []);

    const deleteFromFolder = useCallback(async (fileName) => {
        if (!handleRef.current) return false;
        return deleteOutboxFile(handleRef.current, fileName);
    }, []);

    const getFolderFile = useCallback(async (fileName) => {
        try {
            if (!handleRef.current) return null;
            const fh = await handleRef.current.getFileHandle(fileName);
            return await fh.getFile();
        } catch { return null; }
    }, []);

    // Beim Login automatisch den gemerkten Ordner wieder verbinden (wenn Rechte bestehen).
    useEffect(() => {
        if (!isDealer || !scanFolderSupported()) return undefined;
        let cancelled = false;
        (async () => {
            const stored = await getStoredDocCenterFolder();
            if (cancelled || !stored) return;
            if (stored.needsPermission) {
                setFolderName(stored.name || "");
                setNeedsReconnect(true);
                return;
            }
            handleRef.current = stored;
            setFolderName(stored.name);
            setWatching(true);
        })();
        return () => { cancelled = true; };
    }, [isDealer]);

    const book = useCallback(async (item, force = false, opts = {}) => {
        const interactive = !opts.background;
        setBooking(item.id);
        try {
            const rows = item.docType === "control_only" ? [] : (item.scan.items || []).map((m) => ({
                action: m.match ? "match" : "create",
                article_id: m.match?.article_id || null,
                sku: m.match?.sku || m.scanned?.sku || "",
                name: m.scanned?.name || "",
                quantity: m.scanned?.quantity || 0,
                unit: m.scanned?.unit || "Stück",
                purchase_price_net: m.scanned?.unit_price_net || m.match?.purchase_price_net || null,
                uvp_gross: m.scanned?.uvp_gross || null,
                reorder_item_id: m.reorder_match?.item_id || null,
            }));
            const { data } = await api.post("/articles/scan-delivery-note/commit", {
                file_name: item.fileName,
                supplier_name: item.scan.supplier_name,
                delivery_note_number: item.scan.delivery_note_number,
                invoice_number: item.scan.invoice_number || null,
                total_gross: item.scan.total_gross || null,
                items: rows,
                recipient_name: item.scan.recipient_name,
                is_commission: item.commissionMode,
                consignment_target: item.commissionMode ? item.commissionTarget : null,
                doc_type: item.docType,
                fingerprint: item.scan.fingerprint,
                file_url: item.scan.file_url || null,
                file_storage_path: item.scan.file_storage_path || null,
                customer_number: item.scan.customer_number || null,
                supplier_account_match: item.scan.supplier_account_match || null,
                target_dealer_id: item.scan.supplier_account_match?.dealer_id || null,
                origin: "inbox",
                force,
            });
            setPendingSync((prev) => prev.filter((p) => p.id !== item.id));
            const removed = await deleteIfNoPending(item.fileName);
            addLog({
                ok: true, channel: "inbox", name: item.fileName, docType: item.docType,
                supplier: item.scan.supplier_name, commission: item.commissionMode,
                number: item.scan.delivery_note_number || item.scan.invoice_number,
                fileUrl: item.scan.file_url,
                booked: data.count_booked, created: data.count_created,
                accounting: data.forwarded_to_accounting, removed,
            });
            setHistoryReload((n) => n + 1);
            toast.success(
                item.docType === "control_only"
                    ? `${item.fileName}: für Rechnungskontrolle abgelegt (Buchhaltung → Kreditoren)`
                    : `${item.fileName}: ${data.count_booked} gebucht, ${data.count_created} neu angelegt`
                    + (item.commissionMode ? " · KOMMISSION" : "")
                    + (data.forwarded_to_accounting ? " · an Buchhaltung übergeben" : "")
                    + (removed ? " · aus Ordner gelöscht" : ""));
            return true;
        } catch (e) {
            const det = e.response?.data?.detail;
            if (e.response?.status === 409 && det?.code === "possible_duplicate") {
                setBooking(null);
                if (!interactive) {
                    // Hintergrund-Verarbeitung: bereits verbucht → Datei entfernen, NICHT doppelt buchen.
                    setPendingSync((prev) => prev.filter((p) => p.id !== item.id));
                    const removed = await deleteIfNoPending(item.fileName);
                    addLog({ ok: true, channel: "inbox", duplicate: true, name: item.fileName, docType: item.docType, supplier: item.scan.supplier_name,
                        number: item.scan.delivery_note_number || item.scan.invoice_number, fileUrl: item.scan.file_url, removed });
                    toast.info(`${item.fileName}: Beleg war bereits verbucht (Doppel-Scan erkannt)`);
                    return true;
                }
                const d = det.duplicate || {};
                if (window.confirm(`${det.message}\n\nBereits gebucht: ${d.supplier_name || ""} ${d.delivery_note_number || ""} (${(d.created_at || "").slice(0, 10)}).\n\nTrotzdem erneut buchen?`)) {
                    return book(item, true, opts);
                }
                setPendingSync((prev) => prev.some((p) => p.id === item.id) ? prev : [...prev, item]);
                return false;
            }
            if (interactive) toast.error((typeof det === "string" && det) || det?.message || "Buchung fehlgeschlagen");
            else addLog({ ok: false, name: item.fileName, error: (typeof det === "string" && det) || det?.message || "Buchung fehlgeschlagen" });
            setPendingSync((prev) => prev.some((p) => p.id === item.id) ? prev : [...prev, item]);
            return false;
        } finally {
            setBooking(null);
        }
    }, [addLog, deleteIfNoPending, setPendingSync]);

    // ── Kunden-Dokument (Postausgangs-Pipeline) verarbeiten ──────────────────
    // Aug-2026 (User-Wunsch): ZB1/Werkstattauftrag/Versicherungsschein → am PC
    // automatisch zur Scan-/Auftragserfassung wechseln, um den Vorgang zu
    // komplettieren (Datei wird übergeben; Löschen erst nach Erfolg).
    const AUTO_ERFASSUNG_TYPES = ["zb1", "zb2", "coc", "werkstattauftrag", "versicherungsschein", "kfz_versicherung"];
    const handleCustomerDoc = useCallback(async (data, fileName, file = null) => {
        const item = { ...data, fileName };
        const dt = (item.document_type || "").toLowerCase();
        if (file && AUTO_ERFASSUNG_TYPES.includes(dt)) {
            setHandoffCleanup(file, () => deleteFromFolder(fileName));
            setHandoffFile(file);
            toast.info(`${fileName}: ${dt.toUpperCase()} erkannt — Scan-Erfassung wird geöffnet …`);
            navigate("/dealer/workshop-orders?action=zb1-scan");
            return;
        }
        // Lohn/Ausweis/Pass → automatisch in die „Upload Belege" des Verkaufs.
        if (autoBookRef.current && item.suggested_action === "belege_upload"
            && (item.matches || []).length === 1 && item.matches[0].customer_id) {
            try {
                const { data: r } = await api.post("/documents/outbox/to-belege", {
                    token: item.token, customer_id: item.matches[0].customer_id,
                });
                const removed = await deleteIfNoPending(fileName);
                addLog({ ok: true, channel: "outbox", name: fileName,
                    action: `Upload-Center: „${r.label}" → Verkauf${r.vehicle_label ? ` ${r.vehicle_label}` : ""} (${r.customer_name})`, removed });
                setHistoryReload((n) => n + 1);
                toast.success(`${fileName} → Upload-Belege „${r.label}" (${r.customer_name})`);
                return;
            } catch { /* → manuelle Zuordnung */ }
        }
        // Eindeutige Kunden-Zuordnung → automatisch archivieren (+ E-Mail je Regel).
        const mode = item.email_mode || "ask";
        if (autoBookRef.current && item.auto_match && !item.is_supplier_doc && mode !== "ask") {
            try {
                const { data: r } = await api.post("/documents/outbox/commit", {
                    token: item.token,
                    customer_id: item.auto_match.customer_id,
                    vehicle_id: item.auto_match.vehicle_id || null,
                    send_email: mode === "auto" || mode === "evening",
                });
                const removed = await deleteIfNoPending(fileName);
                addLog({ ok: true, channel: "outbox", name: fileName,
                    customer: item.auto_match.customer_name, vehicle: item.auto_match.vehicle_label,
                    emailed: r.emailed, emailStatus: r.email_status, to: r.to_email, mocked: r.mocked, removed });
                setHistoryReload((n) => n + 1);
                toast.success(`${fileName} → ${item.auto_match.customer_name}`
                    + (r.emailed ? ` · E-Mail an ${r.to_email}` : "")
                    + (removed ? " · aus Ordner gelöscht" : ""));
                return;
            } catch { /* → manuelle Zuordnung */ }
        }
        setCustomerSync((prev) => prev.some((p) => p.token === item.token) ? prev : [...prev, item]);
        toast.info(`${fileName}: Kunden-Dokument wartet auf Zuordnung im Dokumenten-Center`);
    }, [addLog, deleteFromFolder, deleteIfNoPending, navigate, setCustomerSync]);

    // ── Lieferanten-Beleg-Ergebnis (Lieferschein-Pipeline) verarbeiten ───────
    const handleInboxResult = useCallback(async (data, file) => {
        const docs = data?.multi && Array.isArray(data.documents) ? data.documents : [data];
        let allDone = true;
        const newPending = [];
        for (const d of docs) {
            const fname = d.source_file_name || file.name;
            if (d.maintenance_coupon) {
                // Wartungscoupon — Backend archiviert am Fahrzeug + mailt (abends gesammelt).
                const rc = d.coupon_result || {};
                if (rc.sent) {
                    addLog({ ok: true, channel: "inbox", name: fname, docType: "maintenance_coupon", supplier: d.maintenance_coupon.customer_name, ownSent: rc.sent_to, evening: rc.queued });
                    toast.success(rc.queued
                        ? `${fname}: Wartungscoupon erkannt — am Fahrzeug gespeichert, Versand an ${rc.sent_to} heute Abend gebündelt`
                        : `${fname}: Wartungscoupon erkannt — an ${rc.sent_to} versendet`);
                } else {
                    addLog({ ok: false, name: fname, error: `Wartungscoupon erkannt, aber nicht versendet: ${rc.reason || "unbekannt"}` });
                    toast.warning(`${fname}: Wartungscoupon erkannt — nicht versendet (${rc.reason || "unbekannt"})`);
                    allDone = false;
                }
                continue;
            }
            if (d.own_invoice) {
                // Eigene Ausgangsrechnung — Backend hat sie an den Kunden gemailt (oder Grund geliefert).
                const r = d.own_invoice_result || {};
                if (r.sent) {
                    addLog({ ok: true, channel: "inbox", name: fname, docType: "own_invoice", supplier: d.own_invoice.customer_name, ownSent: r.sent_to });
                    toast.success(`${fname}: Eigene Rechnung ${d.own_invoice.invoice_number} erkannt — an ${r.sent_to} versendet`);
                } else {
                    addLog({ ok: false, name: fname, error: `Eigene Rechnung ${d.own_invoice.invoice_number} erkannt, aber nicht versendet: ${r.reason || "unbekannt"}` });
                    toast.warning(`${fname}: Eigene Rechnung erkannt — nicht versendet (${r.reason || "unbekannt"})`);
                    allDone = false;
                }
                continue;
            }
            if (d.auto_booked) {
                // Backend hat bereits automatisch verbucht — nichts doppelt buchen.
                addLog({ ok: true, channel: "inbox", name: fname, docType: d.doc_type, supplier: d.supplier_name,
                    number: d.delivery_note_number || d.invoice_number, fileUrl: d.file_url,
                    commission: !!d.is_commission,
                    booked: d.auto_book_summary?.booked || 0, created: d.auto_book_summary?.created || 0,
                    accounting: false });
                toast.success(`${fname}: automatisch verbucht (${d.auto_book_summary?.booked || 0} gebucht, ${d.auto_book_summary?.created || 0} neu)`);
                continue;
            }
            if (d.possible_duplicate && d.auto_book_error) {
                addLog({ ok: true, channel: "inbox", duplicate: true, name: fname, docType: d.doc_type, supplier: d.supplier_name,
                    number: d.delivery_note_number || d.invoice_number, fileUrl: d.file_url });
                continue;
            }
            const item = shapeItem(d, file.name);
            const canAuto = autoBookRef.current && !d.mocked && !item.decisionRequired
                && !d.possible_duplicate
                && ((d.items || []).length > 0 || item.docType === "control_only");
            if (canAuto) {
                // eslint-disable-next-line no-await-in-loop
                const ok = await book(item, false, { background: true });
                if (!ok) allDone = false;
            } else {
                newPending.push(item);
                allDone = false;
                if (d.mocked) toast.warning(`${file.name}: KI-Erkennung fehlgeschlagen — bitte prüfen`);
                else toast.info(`${file.name}: Beleg wartet auf Ihre Prüfung im Dokumenten-Center`);
            }
        }
        if (newPending.length) {
            setPendingSync((prev) => [...prev.filter((p) => p.fileName !== file.name), ...newPending]);
        }
        if (allDone && handleRef.current) {
            await deleteOutboxFile(handleRef.current, file.name);
        }
    }, [addLog, book, setPendingSync]);

    const processFile = useCallback(async (file) => {
        if (file.size > 25 * 1024 * 1024) {
            addLog({ ok: false, name: file.name, error: "Datei zu groß (max 25 MB)" });
            return;
        }
        setPhase(file.name, "upload");
        try {
            // Aug-2026 (User-Report „Cloudflare/Netzwerk-Fehler"): vorübergehende
            // Server-/Netzfehler automatisch bis zu 3× mit Wartezeit wiederholen —
            // erst danach als Fehler ausweisen (Datei geht dabei NICHT verloren).
            let data;
            let lastErr = null;
            for (let attempt = 0; attempt < 3; attempt += 1) {
                try {
                    if (attempt) {
                        setPhase(file.name, "upload");
                        // eslint-disable-next-line no-await-in-loop
                        await new Promise((r) => setTimeout(r, attempt === 1 ? 5000 : 20000));
                    }
                    // eslint-disable-next-line no-await-in-loop
                    data = await runScanJob("auto", file, {
                        params: { origin: "inbox" },
                        onPhase: (p) => setPhase(file.name, p),
                    });
                    lastErr = null;
                    break;
                } catch (e) {
                    lastErr = e;
                    const msg = String(e.response?.data?.detail || e.message || "");
                    const transient = /HTTP 5|Netzwerk|cloudflare|parse|Zeitüberschreitung|erneut versuchen|timeout/i.test(msg);
                    if (!transient) break;
                }
            }
            if (lastErr) throw lastErr;
            failsRef.current.delete(file.name);
            if (data?.channel === "outbox") {
                await handleCustomerDoc(data, file.name, file);
            } else if (data?.channel === "unsure") {
                setUnsureSync((prev) => prev.some((p) => p.fileName === file.name) ? prev : [...prev, {
                    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    fileName: file.name, file,
                    reason: data.classify_reason || "Dokumenttyp nicht eindeutig erkannt",
                    docType: data.document_type,
                }]);
                toast.info(`${file.name}: Dokumenttyp unklar — bitte im Dokumenten-Center entscheiden (Lieferant oder Kunde)`);
            } else {
                await handleInboxResult(data, file);
            }
        } catch (e) {
            // Aug-2026: max. 3 automatische Anläufe pro Datei (Ordner-Watcher),
            // danach bleibt der Fehler stehen — mit „Erneut versuchen"-Button.
            const fails = (failsRef.current.get(file.name) || 0) + 1;
            failsRef.current.set(file.name, fails);
            if (fails < 3) processedRef.current.delete(file.name);
            addLog({ ok: false, name: file.name, retryFile: file,
                error: e.response?.data?.detail || e.message || "Analyse fehlgeschlagen" });
        }
        setPhase(file.name, null);
    }, [addLog, handleCustomerDoc, handleInboxResult, setPhase, setUnsureSync]);

    // Unsicherer Fall → Nutzer entscheidet: Lieferanten-Beleg oder Kunden-Dokument.
    const reprocessUnsure = useCallback(async (u, target) => {
        setUnsureSync((prev) => prev.filter((p) => p.id !== u.id));
        setPhase(u.fileName, "upload");
        try {
            if (target === "supplier") {
                const data = await runScanJob("delivery-note", u.file, {
                    params: { origin: "inbox" }, onPhase: (p) => setPhase(u.fileName, p),
                });
                await handleInboxResult(data, u.file);
            } else {
                const data = await runScanJob("outbox", u.file, { onPhase: (p) => setPhase(u.fileName, p) });
                await handleCustomerDoc(data, u.fileName, u.file);
            }
        } catch (e) {
            addLog({ ok: false, name: u.fileName, retryFile: u.file,
                error: e.response?.data?.detail || e.message || "Analyse fehlgeschlagen" });
        }
        setPhase(u.fileName, null);
    }, [addLog, handleCustomerDoc, handleInboxResult, setPhase, setUnsureSync]);

    const skipUnsure = useCallback((u) => {
        setUnsureSync((prev) => prev.filter((p) => p.id !== u.id));
        processedRef.current.delete(u.fileName);
    }, [setUnsureSync]);

    const removeCustomerItem = useCallback((token) =>
        setCustomerSync((prev) => prev.filter((p) => p.token !== token)), [setCustomerSync]);

    const skipCustomerItem = useCallback((item) => {
        setCustomerSync((prev) => prev.filter((p) => p.token !== item.token));
        processedRef.current.delete(item.fileName);
    }, [setCustomerSync]);

    const pollOnce = useCallback(async () => {
        if (!handleRef.current || busyRef.current) return;
        busyRef.current = true;
        try {
            const files = await listOutboxFiles(handleRef.current);
            const fresh = files.filter((f) => !processedRef.current.has(f.name));
            fresh.forEach((f) => processedRef.current.add(f.name));
            // 2 Dateien parallel — flüssigere Abarbeitung
            for (let i = 0; i < fresh.length; i += 2) {
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(fresh.slice(i, i + 2).map((f) => processFile(f)));
            }
        } catch { /* Ordner ggf. kurz gesperrt */ }
        busyRef.current = false;
    }, [processFile]);

    useEffect(() => {
        if (!watching) return undefined;
        pollOnce();
        const t = setInterval(pollOnce, 4000);
        return () => clearInterval(t);
    }, [watching, pollOnce]);

    // Handy-Uploads (Filial-QR) global abholen — landen als Kunden-Dokumente.
    useEffect(() => {
        if (!isDealer) return undefined;
        const tick = async () => {
            try {
                const { data } = await api.get("/documents/outbox/mobile-uploads");
                for (const job of data.items || []) {
                    // eslint-disable-next-line no-await-in-loop
                    await api.post(`/documents/outbox/mobile-uploads/${job.id}/consume`).catch(() => {});
                    if (job.status === "error") {
                        addLog({ ok: false, name: job.filename || "Handy-Upload", error: job.error || "Analyse fehlgeschlagen" });
                        continue;
                    }
                    const item = { ...(job.result || {}), fileName: job.filename || job.result?.filename || "Handy-Upload", mobile: true };
                    setCustomerSync((prev) => (prev.some((p) => p.token === item.token) ? prev : [...prev, item]));
                    toast.success(`Beleg vom Handy empfangen: ${item.fileName} — bitte Zuordnung prüfen`);
                }
            } catch { /* noop */ }
        };
        tick();
        const t = setInterval(tick, 6000);
        return () => clearInterval(t);
    }, [isDealer, isLeader, addLog, setCustomerSync]);

    const connect = useCallback(async (forcePick = false) => {
        if (!scanFolderSupported()) {
            toast.error("Dieser Browser unterstützt keine Ordner-Überwachung (Chrome/Edge nötig) — Dateien können manuell hochgeladen werden.");
            return;
        }
        try {
            const handle = await getDocCenterFolder(forcePick);
            if (!handle) return;
            handleRef.current = handle;
            setFolderName(handle.name);
            setWatching(true);
            setNeedsReconnect(false);
            toast.success(`Ordner „${handle.name}" verbunden — Überwachung läuft jetzt in der GESAMTEN App`);
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Ordner konnte nicht verbunden werden");
        }
    }, []);

    const stopWatching = useCallback(() => setWatching(false), []);

    const onManualPick = useCallback(async (files) => {
        const list = Array.from(files || []).filter((f) => !processedRef.current.has(f.name));
        list.forEach((f) => processedRef.current.add(f.name));
        for (let i = 0; i < list.length; i += 2) {
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(list.slice(i, i + 2).map((f) => processFile(f)));
        }
    }, [processFile]);

    const updateItem = useCallback((id, patch) =>
        setPendingSync((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p)), [setPendingSync]);

    const skipItem = useCallback((item) => {
        setPendingSync((prev) => prev.filter((p) => p.id !== item.id));
        processedRef.current.delete(item.fileName);
    }, [setPendingSync]);

    // Aug-2026 (User-Wunsch): fehlgeschlagene Datei manuell erneut verarbeiten.
    const retryProcess = useCallback((logEntry) => {
        const f = logEntry?.retryFile;
        if (!f) return;
        failsRef.current.delete(f.name);
        processedRef.current.add(f.name);
        processFile(f);
    }, [processFile]);

    const value = {
        supported: scanFolderSupported(),
        folderName, watching, needsReconnect, isLeader,
        autoBook, setAutoBook,
        pending, pendingCustomer, pendingUnsure,
        analyzing, booking, log, historyReload,
        connect, stopWatching, onManualPick,
        book, updateItem, skipItem, retryProcess,
        reprocessUnsure, skipUnsure,
        removeCustomerItem, skipCustomerItem,
        deleteFromFolder, getFolderFile, addLog,
    };
    return <InboxWatcherContext.Provider value={value}>{children}</InboxWatcherContext.Provider>;
}
